import { z } from 'zod';
import { bpSchema, dateKeySchema, monthSchema, paiseSchema, positivePaiseSchema } from '../money';
import { objectIdSchema, optionalText, paginationQuerySchema, patchOf } from './common';
import { PAYMENT_METHODS } from './projects';

export const SALARY_TYPES = ['monthly', 'daily', 'hourly', 'contract', 'custom'] as const;
export type SalaryType = (typeof SALARY_TYPES)[number];
export const COMPONENT_KINDS = ['earning', 'deduction'] as const;
/**
 * Calculation order inside a structure: fixed → % of gross → % of basic → balance.
 * "balance" takes whatever is left of the monthly gross (e.g. special allowance).
 */
export const COMPONENT_CALCULATIONS = ['fixed', 'percent_of_gross', 'percent_of_basic', 'balance'] as const;
export type ComponentCalculation = (typeof COMPONENT_CALCULATIONS)[number];

export const PAYROLL_STATUSES = ['draft', 'calculated', 'under_review', 'approved', 'partially_paid', 'paid', 'cancelled'] as const;
export type PayrollStatus = (typeof PAYROLL_STATUSES)[number];
export const PAYROLL_LOCKED: PayrollStatus[] = ['approved', 'partially_paid', 'paid'];

export const ADJUSTMENT_TYPES = ['overtime', 'bonus', 'arrears', 'reimbursement', 'other_earning', 'tax', 'other_deduction'] as const;
export type AdjustmentType = (typeof ADJUSTMENT_TYPES)[number];
export const DEDUCTION_ADJUSTMENTS: AdjustmentType[] = ['tax', 'other_deduction'];

export const ADVANCE_STATUSES = ['requested', 'approved', 'rejected', 'disbursed', 'closed', 'cancelled'] as const;
export type AdvanceStatus = (typeof ADVANCE_STATUSES)[number];

const codeSchema = z.string().trim().toUpperCase().regex(/^[A-Z][A-Z0-9_]{0,11}$/, 'Use up to 12 letters, digits or _ (start with a letter)');

// ------------------------------------------------------------------ components & structures
export const salaryComponentSchema = z.object({
  code: codeSchema,
  name: z.string().trim().min(2).max(60),
  kind: z.enum(COMPONENT_KINDS),
  /** Counts towards the PF wage (typically basic and DA — confirm with your payroll adviser). */
  pfWage: z.boolean().default(false),
  /** Reduced proportionally for loss-of-pay days. */
  prorate: z.boolean().default(true),
  taxable: z.boolean().default(true),
  active: z.boolean().default(true),
  order: z.coerce.number().int().min(0).max(1000).default(100),
});
export type SalaryComponentInput = z.input<typeof salaryComponentSchema>;
export const salaryComponentUpdateSchema = patchOf(salaryComponentSchema.omit({ code: true }));

export const structureLineSchema = z
  .object({
    code: codeSchema,
    calculation: z.enum(COMPONENT_CALCULATIONS),
    /** paise for fixed, basis points for percentages, ignored for balance. */
    value: z.number().int().min(0).max(1_000_000_000_000).default(0),
  })
  .refine((l) => !l.calculation.startsWith('percent') || l.value <= 10_000, 'A percentage cannot exceed 100%');

export const salaryStructureSchema = z
  .object({
    name: z.string().trim().min(2).max(80),
    description: optionalText(500),
    lines: z.array(structureLineSchema).min(1).max(30),
    active: z.boolean().default(true),
  })
  .refine((s) => s.lines.filter((l) => l.calculation === 'balance').length <= 1, { message: 'Only one component can take the balance', path: ['lines'] })
  .refine((s) => new Set(s.lines.map((l) => l.code)).size === s.lines.length, { message: 'Each component can appear once', path: ['lines'] });
export type SalaryStructureInput = z.input<typeof salaryStructureSchema>;

// ------------------------------------------------------------------ salary revisions (append-only, effective-dated)
export const salaryRevisionSchema = z
  .object({
    effectiveFrom: dateKeySchema,
    salaryType: z.enum(SALARY_TYPES),
    /** monthly / custom from a structure: monthly gross split by the structure. */
    structureId: objectIdSchema.optional(),
    monthlyGrossPaise: positivePaiseSchema.optional(),
    /** custom (or overriding a structure): explicit monthly amounts per component. */
    components: z.array(z.object({ code: codeSchema, amountPaise: paiseSchema })).max(30).optional(),
    /** daily / hourly rate, or the monthly contract fee. */
    ratePaise: positivePaiseSchema.optional(),
    pfApplicable: z.boolean().default(false),
    reason: z.string().trim().min(3).max(500),
  })
  .refine((v) => !['daily', 'hourly', 'contract'].includes(v.salaryType) || v.ratePaise !== undefined, { message: 'Enter the rate', path: ['ratePaise'] })
  .refine((v) => !['monthly', 'custom'].includes(v.salaryType) || (v.structureId && v.monthlyGrossPaise) || v.components?.length, { message: 'Choose a structure and monthly gross, or enter the components', path: ['components'] });
export type SalaryRevisionInput = z.input<typeof salaryRevisionSchema>;

// ------------------------------------------------------------------ PF rules (effective-dated, no built-in rates)
export const pfRuleSchema = z.object({
  effectiveFrom: monthSchema,
  employeeRateBp: bpSchema,
  employerRateBp: bpSchema,
  /** Part of the employer share that goes to the pension scheme (0 if not applicable). */
  employerPensionRateBp: bpSchema.default(0),
  /** Wage ceiling for contributions (null = no ceiling). */
  wageCeilingPaise: paiseSchema.nullable().default(null),
  applyCeilingToEmployee: z.boolean().default(true),
  /** Who validated these values (name / firm) — required, as the rules are statutory. */
  validatedBy: z.string().trim().min(2).max(120),
  notes: optionalText(1000),
});
export type PfRuleInput = z.input<typeof pfRuleSchema>;

export const payrollSettingsSchema = z.object({
  /** How loss-of-pay days are valued: per working day of the month, per calendar day, or per 1/30. */
  lopBasis: z.enum(['working_days', 'calendar_days', 'fixed_30']).default('working_days'),
  /** Pay approved / payable sales incentives through payroll. */
  includeCommissions: z.boolean().default(true),
  payslipFooter: optionalText(500),
  /** Payment day of the following month (for information on payslips). */
  payDay: z.coerce.number().int().min(1).max(28).default(1),
});
export type PayrollSettingsInput = z.input<typeof payrollSettingsSchema>;

// ------------------------------------------------------------------ payroll runs
export const payrollRunCreateSchema = z.object({ month: monthSchema, notes: optionalText(1000) });
export const payrollAdjustmentSchema = z.object({
  type: z.enum(ADJUSTMENT_TYPES),
  label: z.string().trim().min(2).max(80),
  /** Always positive; earnings add, deduction types subtract. */
  amountPaise: positivePaiseSchema,
  note: optionalText(300),
});
export const payrollLineUpdateSchema = z.object({
  adjustments: z.array(payrollAdjustmentSchema).max(20).optional(),
  /** Extra unpaid absence (half-day units) not covered by leave records. */
  manualLopUnits: z.coerce.number().int().min(0).max(124).optional(),
  /** Hours for hourly staff, in hundredths (e.g. 16050 = 160.5 h). */
  hoursHundredths: z.coerce.number().int().min(0).max(100_000).nullable().optional(),
  excluded: z.boolean().optional(),
  note: optionalText(500),
});
export const payrollDecisionSchema = z.object({ note: optionalText(1000) });
export const payrollCancelSchema = z.object({ reason: z.string().trim().min(5).max(1000) });
export const salaryPaymentSchema = z
  .object({
    /** Pay these lines; omit to pay every line's full outstanding amount. */
    lines: z.array(z.object({ lineId: objectIdSchema, amountPaise: positivePaiseSchema })).max(1000).optional(),
    method: z.enum(PAYMENT_METHODS),
    reference: z.string().trim().min(2).max(120),
    paidAt: dateKeySchema.optional(),
  });
export const voidPaymentSchema = z.object({ reason: z.string().trim().min(3).max(500) });
export const payrollExportQuerySchema = z.object({ format: z.enum(['xlsx', 'pdf']).default('xlsx'), report: z.enum(['register', 'pf', 'bank']).default('register') });
export const payrollPaymentListQuerySchema = paginationQuerySchema.extend({ month: monthSchema.optional() });
export const payrollRunListQuerySchema = paginationQuerySchema.extend({ year: z.coerce.number().int().optional() });
export const payslipListQuerySchema = z.object({ employeeId: objectIdSchema.optional(), year: z.coerce.number().int().optional() });
export const salaryListQuerySchema = paginationQuerySchema.extend({ departmentId: objectIdSchema.optional() });

// ------------------------------------------------------------------ advances & loans
export const advanceRequestSchema = z.object({
  /** HR may create on behalf of an employee; employees request for themselves. */
  employeeId: objectIdSchema.optional(),
  kind: z.enum(['advance', 'loan']).default('advance'),
  amountPaise: positivePaiseSchema,
  installmentPaise: positivePaiseSchema,
  recoveryStartMonth: monthSchema,
  reason: z.string().trim().min(3).max(500),
}).refine((v) => v.installmentPaise <= v.amountPaise, { message: 'The instalment cannot exceed the amount', path: ['installmentPaise'] });
export const advanceDecisionSchema = z.object({ note: optionalText(500) });
export const advanceDisburseSchema = z.object({ method: z.enum(PAYMENT_METHODS), reference: z.string().trim().min(2).max(120), paidAt: dateKeySchema.optional() });
export const advanceListQuerySchema = paginationQuerySchema.extend({ status: z.enum(ADVANCE_STATUSES).optional(), employeeId: objectIdSchema.optional(), view: z.enum(['mine', 'all']).optional() });

